import {useCallback, useEffect, useRef, useState} from "react";
import {useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import {generateParamsFormLocation} from "../../../constants/dynamic-filter";
import {fetchDocumentCollection} from "../services/document-service";

const DEFAULT_LIMIT = 40;

const getInitialSortParams = () => ({
    columnId: null,
    sortKey: null,
    direction: null,
});

const getInitialOffset = () => {
    if (typeof window === "undefined") {
        return 0;
    }
    const pageNumber = new URLSearchParams(window.location.search).get("page");
    if (pageNumber) {
        const parsed = Number(pageNumber);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return (parsed - 1) * DEFAULT_LIMIT;
        }
    }
    return 0;
};

const getInitialQuery = () => {
    if (typeof window === "undefined") {
        return "";
    }
    const querySearch = new URLSearchParams(window.location.search).get("querySearch");
    return querySearch ?? "";
};

const mergeById = (previous, next) => {
    const knownIds = new Set(previous.map((item) => item.id));
    const uniqueNext = next.filter((item) => item && !knownIds.has(item.id));
    return [...previous, ...uniqueNext];
};

const sanitizeSortColumns = (columns) => {
    if (!Array.isArray(columns)) {
        return [];
    }
    const seen = new Set();
    return columns
        .map((item) => String(item))
        .filter((item) => {
            if (seen.has(item)) {
                return false;
            }
            seen.add(item);
            return true;
        });
};

const arraysEqual = (a, b) => {
    if (a === b) {
        return true;
    }
    if (!Array.isArray(a) || !Array.isArray(b)) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    for (let index = 0; index < a.length; index += 1) {
        if (a[index] !== b[index]) {
            return false;
        }
    }
    return true;
};

export const useDocumentList = ({
    type,
    limit = DEFAULT_LIMIT,
    fetchCollection = fetchDocumentCollection,
    includeRemoveDefaultParam = true,
    includeCompanyTypeParam = true,
}) => {
    const location = useLocation();
    const {loggedCompany, companyType} = useSelector((state) => state.user);

    const companyId = loggedCompany?.id;

    const [documents, setDocuments] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [offset, setOffset] = useState(getInitialOffset);
    const [query, setQuery] = useState(getInitialQuery);
    const [sortParams, setSortParams] = useState(getInitialSortParams);
    const [availableSortColumns, setAvailableSortColumns] = useState([]);
    const availableSortColumnsRef = useRef([]);
    const [dynamicFilters, setDynamicFilters] = useState([]);
    const [activeDynamicFilter, setActiveDynamicFilter] = useState(() => generateParamsFormLocation(location.search));
    const [defaultParams, setDefaultParams] = useState(null);
    const [defaultParamsUsed, setDefaultParamsUsed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [refreshIndex, setRefreshIndex] = useState(0);
    const [lastCompanyType, setLastCompanyType] = useState(companyType);

    const fetchedOnceRef = useRef(false);
    const prevTypeRef = useRef(type);

    const updateAvailableSortColumns = useCallback((columns) => {
        const sanitizedColumns = sanitizeSortColumns(columns);
        setAvailableSortColumns((prev) => {
            if (arraysEqual(prev, sanitizedColumns)) {
                availableSortColumnsRef.current = prev;
                return prev;
            }
            availableSortColumnsRef.current = sanitizedColumns;
            return sanitizedColumns;
        });
    }, []);

    useEffect(() => {
        const nextActiveFilters = generateParamsFormLocation(location.search);
        const typeChanged = prevTypeRef.current !== type;
        prevTypeRef.current = type;

        setOffset(0);
        setDocuments([]);
        setSortParams(getInitialSortParams());
        updateAvailableSortColumns([]);
        fetchedOnceRef.current = false;
        setIsLoading(true);
        setActiveDynamicFilter(nextActiveFilters);
        setDefaultParams(null);
        setDefaultParamsUsed((prev) => {
            if (typeChanged) {
                return false;
            }
            if (nextActiveFilters.length > 0) {
                return true;
            }
            if (!location.search) {
                return false;
            }
            return prev;
        });
    }, [location.search, type, updateAvailableSortColumns]);

    useEffect(() => {
        if (companyType !== lastCompanyType) {
            fetchedOnceRef.current = false;
            setOffset(0);
            setDocuments([]);
            setSortParams(getInitialSortParams());
            updateAvailableSortColumns([]);
            setDefaultParams(null);
            setDefaultParamsUsed(false);
            setActiveDynamicFilter([]);
            setIsLoading(true);
            setLastCompanyType(companyType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyType, updateAvailableSortColumns]);

    const buildQueryParams = useCallback((overrides = {}) => {
        const locationQuery = (new URLSearchParams(location.search ?? "").get("querySearch") ?? "").trim();
        const requestedQuery = overrides.query !== undefined
            ? (overrides.query ?? "").trim()
            : (query ?? "").trim();
        const finalQuery = requestedQuery || locationQuery;

        const params = {
            limit,
            offset: 0,
            query: finalQuery,
        };

        if (includeRemoveDefaultParam) {
            params.remove_default = !defaultParamsUsed;
        }

        if (includeCompanyTypeParam) {
            params.company_type = companyType;
        }

        const sortColumnsSnapshot = availableSortColumnsRef.current;

        if (
            sortParams.sortKey
            && sortParams.direction
            && Array.isArray(sortColumnsSnapshot)
            && sortColumnsSnapshot.includes(sortParams.sortKey)
        ) {
            params.sort_by = sortParams.sortKey;
            params.sort_dir = sortParams.direction;
        }

        if (typeof overrides.offset === "number") {
            params.offset = overrides.offset;
        }

        return params;
    }, [companyType, defaultParamsUsed, includeCompanyTypeParam, includeRemoveDefaultParam, limit, location.search, query, sortParams]);



    useEffect(() => {
        if (!companyId || !type) {
            setDocuments([]);
            setTotalRows(0);
            setIsLoading(false);
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchInitial = async () => {
            const shouldShowLoading = !fetchedOnceRef.current;
            if (shouldShowLoading) {
                setIsLoading(true);
            }
            setDefaultParams(null);

            try {
                const response = await fetchCollection({
                    companyId,
                    type,
                    params: buildQueryParams({offset: 0}),
                    data: activeDynamicFilter ?? [],
                    signal,
                });

                const list = response?.documents ?? [];
                setDocuments(list);
                setTotalRows(response?.total_rows ?? list.length ?? 0);

                if (Array.isArray(response?.filters)) {
                    setDynamicFilters(response.filters);
                } else {
                    setDynamicFilters([]);
                }

                updateAvailableSortColumns(response?.sort_columns);

                if (response?.default_filter && !defaultParamsUsed && generateParamsFormLocation(location.search).length === 0) {
                    setDefaultParams(`?${response.default_filter}`);
                }

                setLastCompanyType(companyType);
                fetchedOnceRef.current = true;
                setOffset(0);
            } catch (error) {
                if (!signal.aborted) {
                    console.error("Errore durante il fetch documenti:", error);
                    setDocuments([]);
                    setTotalRows(0);
                    setDynamicFilters([]);
                }
            } finally {
                if (!signal.aborted) {
                    setIsLoading(false);
                    setIsFetchingMore(false);
                }
            }
        };

        fetchInitial();

        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        companyId,
        type,
        query,
        refreshIndex,
        buildQueryParams,
        activeDynamicFilter,
        location.search,
        updateAvailableSortColumns,
        fetchCollection,
    ]);

    useEffect(() => {
        if (!companyId || !type) {
            return;
        }
        if (offset === 0) {
            return;
        }

        const controller = new AbortController();
        const signal = controller.signal;

        const fetchMore = async () => {
            try {
                const response = await fetchCollection({
                    companyId,
                    type,
                    params: buildQueryParams({offset}),
                    data: activeDynamicFilter ?? [],
                    signal,
                });

                const list = response?.documents ?? [];
                if (typeof response?.total_rows === "number") {
                    setTotalRows(response.total_rows);
                }

                setDocuments((prev) => mergeById(prev, list));
                updateAvailableSortColumns(response?.sort_columns);
            } catch (error) {
                if (!signal.aborted) {
                    console.error("Errore durante il fetch aggiuntivo documenti:", error);
                }
            } finally {
                if (!signal.aborted) {
                    setIsFetchingMore(false);
                }
            }
        };

        fetchMore();

        return () => controller.abort();
    }, [companyId, type, offset, buildQueryParams, activeDynamicFilter, updateAvailableSortColumns, fetchCollection]);

    const handleSearch = useCallback((value) => {
        fetchedOnceRef.current = false;
        setQuery(value ?? "");
        setOffset(0);
        setDocuments([]);
        setIsLoading(true);
    }, []);

    const handleSorting = useCallback((sorting) => {
        let shouldReload = false;

        setSortParams((prev) => {
            const nextColumnId = sorting?.columnId !== undefined && sorting?.columnId !== null
                ? String(sorting.columnId)
                : null;
            const nextSortKey = sorting?.sortKey !== undefined && sorting?.sortKey !== null
                ? String(sorting.sortKey)
                : null;
            const nextDirection = sorting?.sortDir !== undefined && sorting?.sortDir !== null
                ? String(sorting.sortDir)
                : null;

            const same =
                prev.columnId === nextColumnId
                && prev.sortKey === nextSortKey
                && prev.direction === nextDirection;

            if (same) {
                return prev;
            }

            shouldReload = true;

            if (!sorting) {
                return getInitialSortParams();
            }

            return {
                columnId: nextColumnId,
                sortKey: nextSortKey,
                direction: nextDirection,
            };
        });

        if (!shouldReload) {
            return;
        }

        fetchedOnceRef.current = false;
        setOffset(0);
        setDocuments([]);
        setIsLoading(true);
    }, []);

    const handleDynamicFilterChange = useCallback((filters) => {
        fetchedOnceRef.current = false;
        setActiveDynamicFilter(filters ?? []);
        setOffset(0);
        setDocuments([]);
        setDefaultParamsUsed(true);
        setDefaultParams(null);
        setIsLoading(true);
    }, []);

    const handleFetchMore = useCallback((nextOffset) => {
        if (isFetchingMore) {
            return;
        }
        if (documents.length >= totalRows) {
            return;
        }
        setIsFetchingMore(true);
        setOffset(nextOffset);
    }, [documents.length, isFetchingMore, totalRows]);

    const refresh = useCallback(() => {
        fetchedOnceRef.current = false;
        setOffset(0);
        setDocuments([]);
        setIsLoading(true);
        setRefreshIndex((index) => index + 1);
    }, []);

    const removeDocument = useCallback((documentId) => {
        setDocuments((prev) => prev.filter((item) => item.id !== documentId));
        setTotalRows((prev) => (prev > 0 ? prev - 1 : 0));
    }, []);

    return {
        companyId,
        documents,
        totalRows,
        limit,
        offset,
        isLoading,
        isFetchingMore,
        dynamicFilters,
        availableSortColumns,
        defaultParams,
        activeDynamicFilter,
        lastCompanyType,
        handleSearch,
        handleSorting,
        handleDynamicFilterChange,
        handleFetchMore,
        refresh,
        removeDocument,
    };
};
